import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import React, { useEffect, useMemo, useState } from "react"
import { FaSearch, FaEye } from "react-icons/fa"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import { fetchData } from "utils/Common_api_functions"

// ⭐ Rating stars
const StarRating = ({ rating }) => {
  return (
    <div>
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          style={{
            fontSize: 20,
            color: i <= rating ? "#ffc107" : "#ddd",
          }}
        >
          ★
        </span>
      ))}
    </div>
  )
}

const FeedbackTable = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [searchString, setSearchString] = useState("")
  const [perPageItem, setPerPageItem] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [selectedFeedback, setSelectedFeedback] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    fetchFeedback()
  }, [])

  async function fetchFeedback() {
    const APIdata = await fetchData(setLoading, "feedback")
    if (APIdata?.success) {
      setData(APIdata?.data)
    }
  }

  /* ================= FILTER ================= */
  const filteredData = useMemo(() => {
    if (!searchString) return data

    const query = searchString.toLowerCase()

    return data.filter(item => {
      const searchableText = [
        item?.id,
        item?.employee?.name,
        item?.location?.name,
        item?.location?.address,
        item?.checkedAndAuthorisedBy,
        item?.position,
        item?.isWorkSatisfied ? "yes" : "no",
      ]
        .filter(Boolean) // remove null/undefined
        .join(" ")
        .toLowerCase()

      return searchableText.includes(query)
    })
  }, [data, searchString])

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredData.length / perPageItem)
  const start = (currentPage - 1) * perPageItem
  const paginatedData = filteredData.slice(start, start + perPageItem)

  /* ================= MODAL ================= */
  const openModal = item => {
    setSelectedFeedback(item)
    setModalOpen(true)
  }

  return (
    <div className="page-content">
      <Container fluid>
        {loading && <Loader />}

        <Breadcrumb
          title="Dashboard"
          breadcrumbItem="Window Cleaner Feedback"
          description="View all Window Cleaner Client's Feedback"
        />

        <Row>
          <Col lg="12">
            <Card className="shadow-lg">
              <CardBody>
                {/* SEARCH + PAGE SIZE */}
                <div className="d-flex justify-content-start mb-3 flex-wrap gap-3">
                  <div className="position-relative">
                    <input
                      className="form-control rounded-4 ps-3 pe-5"
                      placeholder="Search feedback..."
                      value={searchString}
                      onChange={e => {
                        setSearchString(e.target.value)
                        setCurrentPage(1)
                      }}
                    />
                    <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                  </div>

                  <select
                    className="form-select rounded-4 w-auto"
                    value={perPageItem}
                    onChange={e => {
                      setPerPageItem(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                {/* TABLE */}
                <Table bordered hover responsive>
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Employee</th>
                      <th>Location</th>
                      <th>Authority</th>
                      <th>Position</th>
                      <th>Work Satisfaction</th>
                      <th>View</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          No feedback found
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map(item => (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td>{item?.employee?.name}</td>
                          <td>{item?.location?.name}</td>
                          <td>{item?.checkedAndAuthorisedBy}</td>
                          <td>{item?.position}</td>
                          <td>{item?.isWorkSatisfied ? "Yes" : "No"}</td>
                          <td>
                            <Button
                              size="sm"
                              color="info"
                              onClick={() => openModal(item)}
                            >
                              <FaEye />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>

                {/* PAGINATION */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => p - 1)}
                    >
                      Prev
                    </Button>

                    <span className="align-self-center">
                      Page {currentPage} of {totalPages}
                    </span>

                    <Button
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ================= MODAL ================= */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setModalOpen(false)}>
          Feedback Details
        </ModalHeader>

        <ModalBody>
          {selectedFeedback && (
            <div className="feedback-modal">
              {/* ================= HEADER CARD ================= */}
              <div className="card shadow-sm mb-4 border-0">
                <div className="card-body d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1 fw-bold">
                      {selectedFeedback.employee?.name}
                    </h5>
                    <p className="mb-0 text-muted">
                      {selectedFeedback.location?.name}
                    </p>
                    <small className="text-muted">
                      {selectedFeedback.location?.address}
                    </small>
                  </div>

                  <span
                    className={`badge px-3 py-2 ${
                      selectedFeedback.isWorkSatisfied
                        ? "bg-success"
                        : "bg-danger"
                    }`}
                  >
                    {selectedFeedback.isWorkSatisfied
                      ? "Work Satisfied"
                      : "Not Satisfied"}
                  </span>
                </div>
              </div>

              {/* ================= CLEANING DETAILS ================= */}
              <div className="card shadow-sm mb-4 border-0">
                <div className="card-body">
                  <h6 className="fw-bold mb-3 text-primary">
                    Cleaning Checklist
                  </h6>

                  <div className="row g-3">
                    {[
                      {
                        label: "Window Cleaning",
                        value: selectedFeedback.isWindowCleaning,
                      },
                      {
                        label: "Outside Windows",
                        value: selectedFeedback.hasCleanedOutsideWindow,
                      },
                      {
                        label: "Inside Windows",
                        value: selectedFeedback.hasCleanedInsideWindow,
                      },
                      {
                        label: "Big Clean",
                        value: selectedFeedback.isBigClean,
                      },
                    ].map((item, index) => (
                      <div className="col-md-6" key={index}>
                        <div className="d-flex justify-content-between align-items-center p-3 rounded bg-light">
                          <span className="fw-medium">{item.label}</span>
                          <span
                            className={`badge ${
                              item.value ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {item.value ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ================= AUTHORIZATION ================= */}
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h6 className="fw-bold mb-3 text-primary">Authorization</h6>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <small className="text-muted">
                        Checked & Authorised By
                      </small>
                      <p className="fw-semibold mb-0">
                        {selectedFeedback.checkedAndAuthorisedBy}
                      </p>
                    </div>

                    <div className="col-md-6 mb-3">
                      <small className="text-muted">Position</small>
                      <p className="fw-semibold mb-0">
                        {selectedFeedback.position}
                      </p>
                    </div>
                  </div>

                  <div>
                    <small className="text-muted">Signature</small>
                    <p className="mb-0 text-muted">
                      {selectedFeedback.signature}
                    </p>
                  </div>

                  <hr />

                  <small className="text-muted">
                    Submitted on{" "}
                    {new Date(selectedFeedback.createdAt).toLocaleString()}
                  </small>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default FeedbackTable
